import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import AdminVoice from "./AdminVoice";
import { toast } from "sonner";
import {
  Users2, Building2, MessageSquare, Target, X, Save, ShieldAlert, Facebook, Loader2, IndianRupee,
  KeyRound, CheckCircle2, XCircle, Trash2, RefreshCw, Plus, Upload,
} from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "users", label: "Users & Workspaces" },
  { k: "billing", label: "Usage & Billing" },
  { k: "platform", label: "1-Click / Platform" },
  { k: "keys", label: "API Keys" },
  { k: "voice", label: "Voice Calls" },
  { k: "import", label: "Import Data" },
];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#E9EDEF] bg-white rounded-lg p-5">
      <Icon className="h-4 w-4 text-[#8696A0] mb-6" />
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{label}</p>
      <p className="font-heading text-3xl font-light text-[#111B21] mt-1">{value}</p>
    </div>
  );
}

function WorkspaceDrawer({ wsId, onClose }) {
  const [data, setData] = useState(null);
  const [integ, setInteg] = useState({ app_id: "", app_secret: "", business_account_id: "", phone_number_id: "", business_phone: "", access_token: "", graph_api_version: "v22.0" });
  const [agent, setAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/workspaces/${wsId}`).then((r) => {
      setData(r.data);
      if (r.data.integration) setInteg((c) => ({ ...c, ...r.data.integration, app_secret: "", access_token: "" }));
      if (r.data.agent) setAgent({ ...r.data.agent, api_key: "" });
    });
  }, [wsId]);

  const saveInteg = async () => {
    setSaving(true);
    try {
      const p = { ...integ };
      if (!p.app_secret) delete p.app_secret;
      if (!p.access_token) delete p.access_token;
      await api.put(`/admin/workspaces/${wsId}/integration`, p);
      toast.success("WhatsApp config saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const saveAgent = async () => {
    setSaving(true);
    try {
      const p = {
        name: agent.name, role: agent.role, personality: agent.personality, tone: agent.tone,
        language: agent.language, instructions: agent.instructions, objectives: agent.objectives,
        escalation_rules: agent.escalation_rules, model_provider: agent.model_provider,
        model_name: agent.model_name, temperature: parseFloat(agent.temperature) || 0.7,
        enabled: agent.enabled,
      };
      if (agent.api_key) p.api_key = agent.api_key;
      await api.put(`/admin/workspaces/${wsId}/agent`, p);
      toast.success("AI config saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-workspace-drawer">
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#F0F2F5] border-l border-[#E9EDEF] h-full overflow-y-auto">
        <div className="h-16 flex items-center justify-between px-6 border-b border-[#E9EDEF] sticky top-0 bg-[#F0F2F5] z-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">Workspace</p>
            <h3 className="font-heading text-lg text-[#111B21]">{data?.workspace?.name || wsId}</h3>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-[#8696A0] hover:text-[#111B21]"><X className="h-5 w-5" /></button>
        </div>
        {!data ? (<div className="p-8 text-sm text-[#8696A0]">Loading…</div>) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.counts).map(([k, v]) => (
                <div key={k} className="border border-[#E9EDEF] rounded-lg p-3">
                  <p className="font-mono text-[9px] uppercase text-[#8696A0]">{k}</p>
                  <p className="font-heading text-xl text-[#111B21]">{v}</p>
                </div>
              ))}
            </div>

            {/* WhatsApp config */}
            <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3">
              <h4 className="font-heading text-sm text-[#111B21]">WhatsApp Configuration</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["app_id","Meta App ID"],["app_secret","App Secret"],["business_account_id","WABA ID"],["phone_number_id","Phone Number ID"],["business_phone","Business Phone"],["access_token","Access Token"]].map(([k,l]) => (
                  <div key={k}>
                    <label className="font-mono text-[10px] uppercase text-[#8696A0]">{l}</label>
                    <input data-testid={`admin-wa-${k}`} type={k.includes("secret")||k.includes("token")?"password":"text"}
                      value={integ[k] || ""} placeholder={integ[`${k.replace("app_secret","app_secret").replace("access_token","access_token")}_masked`] || ""}
                      onChange={(e) => setInteg({ ...integ, [k]: e.target.value })} className={inputCls + " mt-1 font-mono"} />
                  </div>
                ))}
              </div>
              <button data-testid="admin-save-wa" onClick={saveInteg} disabled={saving}
                className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" /> Save WhatsApp
              </button>
            </div>

            {/* LLM / Agent config */}
            {agent && (
              <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3">
                <h4 className="font-heading text-sm text-[#111B21]">LLM / AI Agent</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase text-[#8696A0]">Provider</label>
                    <select value={agent.model_provider} onChange={(e) => setAgent({ ...agent, model_provider: e.target.value })} className={inputCls + " mt-1"}>
                      <option value="anthropic">Anthropic</option>
                      <option value="openai">OpenAI</option>
                      <option value="gemini">Gemini</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-[#8696A0]">Model</label>
                    <input value={agent.model_name} onChange={(e) => setAgent({ ...agent, model_name: e.target.value })} className={inputCls + " mt-1 font-mono"} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-[#8696A0]">Temperature</label>
                    <input type="number" step="0.1" min="0" max="1" value={agent.temperature ?? 0.7} onChange={(e) => setAgent({ ...agent, temperature: e.target.value })} className={inputCls + " mt-1 font-mono"} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-[#8696A0]">Own LLM API Key {agent.has_own_key ? "(set)" : "(using platform key)"}</label>
                    <input type="password" placeholder={agent.has_own_key ? "•••••• (leave blank to keep)" : "optional"} value={agent.api_key} onChange={(e) => setAgent({ ...agent, api_key: e.target.value })} className={inputCls + " mt-1 font-mono"} />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase text-[#8696A0]">Instructions</label>
                  <textarea rows={3} value={agent.instructions} onChange={(e) => setAgent({ ...agent, instructions: e.target.value })} className={inputCls + " mt-1"} />
                </div>
                <button data-testid="admin-save-agent" onClick={saveAgent} disabled={saving}
                  className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50">
                  <Save className="h-4 w-4" /> Save AI Config
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BillingTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/usage").then((r) => setData(r.data)).catch(() => setData({ workspaces: [], overall_llm: {} })); }, []);
  if (!data) return <div className="text-sm text-[#8696A0]">Loading usage…</div>;
  const o = data.overall_llm || {};
  return (
    <div className="space-y-4" data-testid="admin-billing">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={IndianRupee} label="Total LLM Cost (INR)" value={`₹${(o.total_cost_inr ?? 0).toLocaleString("en-IN")}`} />
        <Stat icon={MessageSquare} label="Total AI Calls" value={o.total_calls ?? 0} />
        <Stat icon={Target} label="Total Tokens" value={(o.total_tokens ?? 0).toLocaleString("en-IN")} />
      </div>
      <p className="text-xs text-[#8696A0]">LLM cost is the real consumed provider cost (from actual token usage × your configured rates). WhatsApp is an <b>ESTIMATE</b> — confirm against Meta's invoice. Set rates in the Platform tab.</p>
      <div className="border border-[#E9EDEF] bg-white rounded-lg overflow-x-auto">
        <table className="w-full min-w-[820px] text-left" data-testid="admin-billing-table">
          <thead>
            <tr className="border-b border-[#E9EDEF] font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">
              <th className="px-5 py-3 font-medium">Workspace</th>
              <th className="px-5 py-3 font-medium">LLM Cost ₹</th>
              <th className="px-5 py-3 font-medium">Tokens</th>
              <th className="px-5 py-3 font-medium">AI Calls</th>
              <th className="px-5 py-3 font-medium">WA Msgs (in/out)</th>
              <th className="px-5 py-3 font-medium">WA Templates</th>
              <th className="px-5 py-3 font-medium">WA Est ₹</th>
              <th className="px-5 py-3 font-medium">Total Est ₹</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9EDEF]">
            {data.workspaces.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-[#8696A0]">No usage recorded yet.</td></tr>
            )}
            {data.workspaces.map((w) => (
              <tr key={w.workspace_id} className="hover:bg-[#F0F2F5] transition-colors">
                <td className="px-5 py-3 text-sm text-[#111B21]">{w.workspace_name || w.workspace_id}
                  <div className="text-[10px] text-[#8696A0]">{(w.llm_per_model || []).map((m) => `${m.model}: ₹${m.cost_inr}`).join(" · ") || "—"}</div>
                </td>
                <td className="px-5 py-3 text-sm font-mono text-[#075E54]">₹{w.llm_cost_inr}</td>
                <td className="px-5 py-3 text-sm font-mono text-[#54656F]">{(w.llm_tokens || 0).toLocaleString("en-IN")}</td>
                <td className="px-5 py-3 text-sm font-mono text-[#54656F]">{w.llm_calls}</td>
                <td className="px-5 py-3 text-sm font-mono text-[#54656F]">{w.wa.inbound_messages}/{w.wa.outbound_messages}</td>
                <td className="px-5 py-3 text-sm font-mono text-[#54656F]">{w.wa.template_sends}</td>
                <td className="px-5 py-3 text-sm font-mono text-[#54656F]">₹{w.wa.est_cost_inr}</td>
                <td className="px-5 py-3 text-sm font-mono font-semibold text-[#111B21]">₹{w.total_est_inr}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlatformTab() {
  const [cfg, setCfg] = useState({ meta_app_id: "", meta_config_id: "", meta_app_secret: "", has_app_secret: false, llm_rates: {}, wa_rates: {} });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/platform-config").then((r) => setCfg((c) => ({ ...c, ...r.data, meta_app_secret: "" }))); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const p = { meta_app_id: cfg.meta_app_id, meta_config_id: cfg.meta_config_id, llm_rates: cfg.llm_rates, wa_rates: cfg.wa_rates };
      if (cfg.meta_app_secret) p.meta_app_secret = cfg.meta_app_secret;
      await api.put("/admin/platform-config", p);
      toast.success("Platform config & billing rates saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const setLlm = (k, v) => setCfg((c) => ({ ...c, llm_rates: { ...c.llm_rates, [k]: v === "" ? "" : Number(v) } }));
  const setWa = (k, v) => setCfg((c) => ({ ...c, wa_rates: { ...c.wa_rates, [k]: v === "" ? "" : Number(v) } }));
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-6 space-y-4" data-testid="admin-platform">
        <div className="flex items-center gap-2">
          <Facebook className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm text-[#111B21]">Meta Embedded Signup (1-Click WhatsApp Connect)</h3>
        </div>
        <p className="text-xs text-[#8696A0]">Enter your Meta App credentials for Facebook Login for Business (Embedded Signup v4). The App Secret is stored encrypted and never returned.</p>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#8696A0]">Meta App ID</label>
          <input data-testid="platform-app-id" value={cfg.meta_app_id} onChange={(e) => setCfg({ ...cfg, meta_app_id: e.target.value })} className={inputCls + " mt-1 font-mono"} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#8696A0]">Embedded Signup Config ID</label>
          <input data-testid="platform-config-id" value={cfg.meta_config_id} onChange={(e) => setCfg({ ...cfg, meta_config_id: e.target.value })} className={inputCls + " mt-1 font-mono"} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#8696A0]">Meta App Secret {cfg.has_app_secret ? "(set)" : ""}</label>
          <input data-testid="platform-app-secret" type="password" placeholder={cfg.has_app_secret ? "•••••• (leave blank to keep)" : ""} value={cfg.meta_app_secret} onChange={(e) => setCfg({ ...cfg, meta_app_secret: e.target.value })} className={inputCls + " mt-1 font-mono"} />
        </div>
      </div>

      <div className="border border-[#E9EDEF] bg-white rounded-lg p-6 space-y-4" data-testid="admin-billing-rates">
        <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm text-[#111B21]">Billing rates (INR)</h3></div>
        <p className="text-xs text-[#8696A0]">LLM: real provider cost in ₹ per 1,000,000 tokens (per model). WhatsApp: Meta's ₹ per 24-hour conversation, by category (India). These drive the Usage &amp; Billing figures.</p>
        <div>
          <p className="font-mono text-[10px] uppercase text-[#8696A0] mb-1">LLM — ₹ / 1M tokens</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(cfg.llm_rates || {}).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-xs text-[#54656F] font-mono w-32 truncate" title={k}>{k}</span>
                <input type="number" value={cfg.llm_rates[k]} onChange={(e) => setLlm(k, e.target.value)} className={inputCls + " font-mono"} data-testid={`llm-rate-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase text-[#8696A0] mb-1">WhatsApp — ₹ / conversation</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(cfg.wa_rates || {}).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-xs text-[#54656F] font-mono w-32 capitalize">{k}</span>
                <input type="number" step="0.01" value={cfg.wa_rates[k]} onChange={(e) => setWa(k, e.target.value)} className={inputCls + " font-mono"} data-testid={`wa-rate-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <button data-testid="platform-save" onClick={save} disabled={saving}
        className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Platform Config &amp; Rates
      </button>
    </div>
  );
}

const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};
const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

function KeyStatus({ k, env }) {
  const t = k?.last_test;
  if (!k) return env
    ? <span className="inline-flex items-center gap-1 text-[11px] text-[#54656F] bg-[#F0F2F5] rounded-full px-2 py-0.5">From server settings</span>
    : <span className="inline-flex items-center gap-1 text-[11px] text-[#8696A0] bg-[#F0F2F5] rounded-full px-2 py-0.5">Not connected</span>;
  if (t?.ok === true) return <span className="inline-flex items-center gap-1 text-[11px] text-[#075E54] bg-[#E7FCE8] rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" />{t.state === "restricted" ? "Working (limited key)" : "Working"}</span>;
  if (t?.ok === false) return <span className="inline-flex items-center gap-1 text-[11px] text-red-700 bg-red-50 rounded-full px-2 py-0.5" title={t.detail}><XCircle className="h-3 w-3" />Failed</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">Saved · not verified</span>;
}

function ProviderCard({ p, k, others, busy, setBusy, reload }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [scope, setScope] = useState("platform");
  const [err, setErr] = useState(null);
  const id = p.provider;
  const save = async (test = true) => {
    if (!val.trim()) return toast.error("Paste the API key first");
    setBusy(`save-${id}`); setErr(null);
    try {
      const r = await api.put("/admin/provider-keys", { provider: id, key: val.trim(), scope: scope || "platform", test });
      const t = r.data?.last_test;
      toast.success(t?.ok ? `${p.label}: ${t.detail}` : `${p.label} key saved`);
      setVal(""); setOpen(false); reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr({ msg: errText(e, "Save failed"), canForce: Boolean(d?.can_save_anyway) });
    } finally { setBusy(""); }
  };
  const test = async (row) => {
    setBusy(`test-${id}-${row.scope}`);
    try { const r = await api.post(`/admin/provider-keys/test`, null, { params: { provider: id, scope: row.scope } }); r.data.ok === false ? toast.error(r.data.detail) : toast.success(r.data.detail || "Key works"); reload(); }
    catch (e) { toast.error(errText(e, "Test failed")); } finally { setBusy(""); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove the ${p.label} key (${row.scope})? Features using it will fall back to another key or stop.`)) return;
    try { await api.delete(`/admin/provider-keys`, { params: { provider: id, scope: row.scope } }); toast.success("Key removed"); reload(); }
    catch (e) { toast.error(errText(e, "Remove failed")); }
  };
  const testEmail = async () => {
    setBusy("email");
    try { const r = await api.post("/admin/provider-keys/test-email"); toast.success(`Test email sent to ${r.data.to} from ${r.data.from}`); }
    catch (e) { toast.error(errText(e, "Test email failed"), { duration: 12000 }); } finally { setBusy(""); }
  };
  return (
    <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 space-y-3" data-testid={`key-card-${id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-[#111B21]">{p.label}</p>
            <KeyStatus k={k} env={p.env_set} />
          </div>
          <p className="text-xs text-[#54656F] mt-1">{p.used_for}</p>
          {k && <p className="text-[11px] font-mono text-[#8696A0] mt-1 break-words">••••{k.last4}{k.last_test?.at ? ` · tested ${when(k.last_test.at)}` : ""}{k.updated_by ? ` · by ${k.updated_by}` : ""}</p>}
          {k?.last_test?.detail && k.last_test.ok !== true && <p className="text-[11px] text-red-700 mt-1">{k.last_test.detail}</p>}
          {k?.last_test?.state === "restricted" && <p className="text-[11px] text-[#54656F] mt-1">{k.last_test.detail}</p>}
          {others.map((o) => <p key={o.scope} className="text-[11px] font-mono text-[#8696A0] mt-1">Workspace {o.scope}: ••••{o.last4}</p>)}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:shrink-0">
          {p.get_url && <a href={p.get_url} target="_blank" rel="noreferrer" className="text-xs text-[#075E54] hover:underline px-1 py-1">Get a key ↗</a>}
          {k && <button onClick={() => test(k)} disabled={!!busy} className="text-xs border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1 inline-flex items-center gap-1" data-testid={`key-test-${id}`}>
            {busy === `test-${id}-${k.scope}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}Test</button>}
          {id === "resend" && k && <button onClick={testEmail} disabled={!!busy} className="text-xs border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1 inline-flex items-center gap-1" data-testid="key-test-email">
            {busy === "email" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}Send test email to me</button>}
          <button onClick={() => { setOpen(!open); setErr(null); }} className="text-xs bg-[#075E54] text-white hover:bg-[#128C7E] rounded-lg px-3 py-1 inline-flex items-center gap-1" data-testid={`key-add-${id}`}>
            <Plus className="h-3 w-3" />{k ? "Replace" : "Add key"}</button>
          {k && <button onClick={() => remove(k)} className="text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-3 py-1 inline-flex items-center gap-1" data-testid={`key-remove-${id}`}>
            <Trash2 className="h-3 w-3" />Remove</button>}
        </div>
      </div>
      {open && (
        <div className="bg-[#F7F8FA] rounded-lg p-3 space-y-2">
          <input type="password" autoComplete="off" value={val} onChange={(e) => { setVal(e.target.value); setErr(null); }}
            onKeyDown={(e) => e.key === "Enter" && save(true)} className={inputCls + " font-mono"}
            placeholder={`Paste your ${p.label} key${p.prefix ? ` (starts with ${p.prefix})` : ""}`} data-testid={`key-input-${id}`} />
          <details className="text-[11px] text-[#54656F]"><summary className="cursor-pointer">Advanced: use only for one workspace</summary>
            <input value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls + " mt-1 font-mono"} placeholder="platform or a workspace id" />
          </details>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err.msg}</div>}
          <div className="flex gap-2">
            <button onClick={() => save(true)} disabled={!!busy} className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50" data-testid={`key-save-${id}`}>
              {busy === `save-${id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Test &amp; save</button>
            {err?.canForce && <button onClick={() => save(false)} disabled={!!busy} className="text-sm border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-4 py-1.5" data-testid={`key-force-${id}`}>Save anyway</button>}
            <button onClick={() => { setOpen(false); setVal(""); setErr(null); }} className="text-sm text-[#54656F] px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function KeysTab() {
  const [data, setData] = useState({ providers: [], groups: [], keys: [] });
  const [busy, setBusy] = useState("");
  const [custom, setCustom] = useState({ name: "", key: "" });
  const load = useCallback(() => api.get("/admin/provider-keys").then((r) => setData(r.data)).catch(() => toast.error("Could not load API keys")), []);
  useEffect(() => { load(); }, [load]);
  const byProv = (id) => data.keys.filter((k) => k.provider === id);
  const groups = (data.groups?.length ? data.groups : ["AI models", "Email", "Voice", "Other"]);
  const customKeys = data.keys.filter((k) => k.provider.startsWith("custom:"));
  const connected = data.providers.filter((p) => byProv(p.provider).length || p.env_set).length;
  const saveCustom = async () => {
    const name = custom.name.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (!name || !custom.key.trim()) return toast.error("Enter a name and the key");
    setBusy("custom");
    try { await api.put("/admin/provider-keys", { provider: `custom:${name}`, key: custom.key.trim(), scope: "platform" }); toast.success(`${name} key saved`); setCustom({ name: "", key: "" }); load(); }
    catch (e) { toast.error(errText(e, "Save failed")); } finally { setBusy(""); }
  };
  const removeCustom = async (k) => {
    if (!window.confirm(`Remove the ${k.provider.replace(/^custom:/, "")} key?`)) return;
    try { await api.delete(`/admin/provider-keys`, { params: { provider: k.provider, scope: k.scope } }); toast.success("Key removed"); load(); }
    catch (e) { toast.error(errText(e, "Remove failed")); }
  };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-keys">
      <div className="flex items-start gap-3">
        <KeyRound className="h-5 w-5 text-[#075E54] mt-0.5" />
        <div>
          <h3 className="font-heading text-sm text-[#111B21]">API keys for MuviArts.Online</h3>
          <p className="text-xs text-[#8696A0] mt-1">{connected} of {data.providers.length} services connected. Keys are encrypted and never shown again after saving. Each key is tested before it's saved; limited keys (like a Resend sending-only key) are accepted. Read-only keys for JARVIS live in Settings → Pipeline.</p>
        </div>
      </div>
      {groups.map((g) => {
        const provs = data.providers.filter((p) => (p.group || "Other") === g);
        if (!provs.length) return null;
        return (
          <div key={g} className="space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{g}</p>
            {provs.map((p) => {
              const rows = byProv(p.provider);
              const main = rows.find((r) => r.scope === "platform") || rows[0];
              return <ProviderCard key={p.provider} p={p} k={main} others={rows.filter((r) => r !== main)} busy={busy} setBusy={setBusy} reload={load} />;
            })}
          </div>
        );
      })}
      <div className="space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Other services</p>
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-4 space-y-3">
          {customKeys.map((k) => (
            <div key={`${k.scope}-${k.provider}`} className="flex items-center justify-between text-sm">
              <span className="text-[#111B21]">{k.provider.replace(/^custom:/, "")} <span className="font-mono text-[11px] text-[#8696A0]">••••{k.last4}</span></span>
              <button onClick={() => removeCustom(k)} className="text-xs text-red-600 hover:underline">Remove</button>
            </div>
          ))}
          {customKeys.length === 0 && <p className="text-xs text-[#8696A0]">No other keys. Add one for any service not listed above (e.g. suno, replicate).</p>}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
            <input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Service name" className={inputCls + " font-mono"} data-testid="key-custom" />
            <input type="password" autoComplete="off" value={custom.key} onChange={(e) => setCustom({ ...custom, key: e.target.value })} placeholder="Paste key" className={inputCls + " font-mono"} data-testid="key-custom-value" />
            <button onClick={saveCustom} disabled={busy === "custom"} className="bg-[#075E54] text-white hover:bg-[#128C7E] rounded-lg px-4 py-2 text-sm disabled:opacity-50">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);
  const run = async (dry) => {
    if (!file) return toast.error("Choose the export file first");
    setBusy(dry ? "check" : "import");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/import-export?dry_run=${dry}`, fd);
      setReport(r.data);
      toast.success(dry ? "Check complete - nothing was changed" : "Import complete");
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(""); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="admin-import">
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3">
        <p className="text-sm text-[#111B21] font-medium">Import a MuviArts.online export</p>
        <p className="text-xs text-[#54656F]">Upload <span className="font-mono">muviarts-online-data-export.json</span>. Check first (changes nothing), then Import.
          Safe to repeat with a newer export: records are updated, not duplicated. Automation stays OFF and imported follow-ups are stopped.</p>
        <input type="file" accept="application/json,.json" onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
          className="block text-sm text-[#54656F]" data-testid="import-file" />
        <div className="flex gap-2">
          <button onClick={() => run(true)} disabled={!!busy} className="text-sm border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-4 py-2 inline-flex items-center gap-2">
            {busy === "check" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check
          </button>
          <button onClick={() => run(false)} disabled={!!busy || !report} className="text-sm bg-[#25D366] hover:bg-[#1FB855] text-white rounded-lg px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50" data-testid="import-run">
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
          </button>
        </div>
      </div>
      {report && (
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3">
          <p className="text-sm font-medium text-[#111B21]">{report.dry_run ? "Check result (nothing changed)" : "Imported"} - export from {report.exported_at}</p>
          <table className="w-full text-sm"><tbody className="divide-y divide-[#E9EDEF]">
            {Object.entries(report.counts || {}).map(([k, v]) => (
              <tr key={k}><td className="py-1.5 text-[#54656F]">{k.replace(/_/g, " ")}</td>
                <td className="py-1.5 font-mono text-right">{v.inserted} new</td><td className="py-1.5 font-mono text-right text-[#8696A0]">{v.updated} updated</td></tr>
            ))}
          </tbody></table>
          <ul className="list-disc pl-5 text-xs text-[#54656F] space-y-1">{(report.notes || []).map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { section } = useParams();
  const active = section || "overview";
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [drawerWs, setDrawerWs] = useState(null);

  const loadUsers = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard", { replace: true }); return; }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    loadUsers();
  }, [user, navigate, loadUsers]);

  if (user && user.role !== "admin") return null;

  return (
    <>
      <PageHeader eyebrow="Administration" title="Admin Panel" />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex gap-1 px-4 md:px-8 pt-4 border-b border-[#E9EDEF] bg-[#F0F2F5] flex-wrap">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => navigate(`/admin/${t.k}`)}
              className={`px-4 py-3 text-sm border-b-2 transition-colors ${active === t.k ? "border-amber-500 text-[#111B21]" : "border-transparent text-[#8696A0] hover:text-[#374151]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 md:p-8">
          {active === "overview" && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Stat icon={Users2} label="Total Users" value={stats?.total_users ?? 0} />
              <Stat icon={Building2} label="Workspaces" value={stats?.total_workspaces ?? 0} />
              <Stat icon={MessageSquare} label="WhatsApp Connected" value={stats?.whatsapp_connected ?? 0} />
              <Stat icon={MessageSquare} label="Total Messages" value={stats?.total_messages ?? 0} />
              <Stat icon={Target} label="Total Leads" value={stats?.total_leads ?? 0} />
              <Stat icon={ShieldAlert} label="WA Configured" value={stats?.whatsapp_configured ?? 0} />
            </div>
          )}

          {active === "users" && (
            <div className="border border-[#E9EDEF] bg-white rounded-lg overflow-x-auto">
              <table className="w-full min-w-[720px] text-left" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-[#E9EDEF] font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Role</th>
                    <th className="px-5 py-3 font-medium">Auth</th>
                    <th className="px-5 py-3 font-medium">Workspace</th>
                    <th className="px-5 py-3 font-medium">WhatsApp</th>
                    <th className="px-5 py-3 font-medium">Msgs</th>
                    <th className="px-5 py-3 font-medium">Leads</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E9EDEF]">
                  {users.map((u) => (
                    <tr key={u.user_id} className="hover:bg-[#F0F2F5] transition-colors">
                      <td className="px-5 py-3">
                        <p className="text-sm text-[#111B21]">{u.name}</p>
                        <p className="text-xs text-[#8696A0]">{u.email}</p>
                      </td>
                      <td className="px-5 py-3"><span className={`font-mono text-[10px] px-2 py-0.5 rounded-lg border ${u.role === "admin" ? "bg-amber-500/10 text-amber-600 border-amber-500/20" : "bg-[#F0F2F5] text-[#54656F] border-[#CBD5E1]"}`}>{u.role}</span></td>
                      <td className="px-5 py-3 text-xs text-[#8696A0]">{u.auth_type}</td>
                      <td className="px-5 py-3 text-sm text-[#54656F]">{u.workspace_name || "—"}</td>
                      <td className="px-5 py-3 text-xs font-mono text-[#54656F]">{u.whatsapp_status}</td>
                      <td className="px-5 py-3 text-sm font-mono text-[#54656F]">{u.message_count}</td>
                      <td className="px-5 py-3 text-sm font-mono text-[#075E54]">{u.lead_count}</td>
                      <td className="px-5 py-3">
                        {u.workspace_id && (
                          <button data-testid={`admin-manage-${u.user_id}`} onClick={() => setDrawerWs(u.workspace_id)}
                            className="text-xs border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1 transition-colors">Manage</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {active === "billing" && <BillingTab />}
          {active === "platform" && <PlatformTab />}
          {active === "keys" && <KeysTab />}
          {active === "voice" && <AdminVoice />}
          {active === "import" && <ImportTab />}
        </div>
      </div>
      {drawerWs && <WorkspaceDrawer wsId={drawerWs} onClose={() => setDrawerWs(null)} />}
    </>
  );
}
